import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Avatar, Card, ErrorNotice } from '@/components/ui';
import { useI18n, type TranslationKey } from '@/i18n';
import { api, type PersonalDetails, type User } from '@/lib/api';
import { dateShort, fullName } from '@/lib/format';

import { BadgeDialog } from './BadgeDialog';

/**
 * The person's record as the office keeps it: the badge photo, identity (date of birth,
 * tax code, hire date, badge number) and contact details. The employee sees the same
 * fields in the app and can change their own phone, address and emergency contact; the
 * rest is set here.
 */

type FieldKey = keyof PersonalDetails;

const FIELDS: { key: FieldKey; type?: 'date'; wide?: boolean }[] = [
  { key: 'firstName' },
  { key: 'lastName' },
  { key: 'jobTitle' },
  { key: 'phone' },
  { key: 'dateOfBirth', type: 'date' },
  { key: 'birthPlace' },
  { key: 'taxCode' },
  { key: 'hiredOn', type: 'date' },
  { key: 'badgeNumber' },
  { key: 'address', wide: true },
  { key: 'emergencyContact', wide: true },
];

function initial(u: User): Record<FieldKey, string> {
  return Object.fromEntries(FIELDS.map(({ key }) => [key, (u[key] as string | null | undefined) ?? ''])) as Record<
    FieldKey,
    string
  >;
}

export function PersonalDetailsCard({ user, canEdit }: { user: User; canEdit: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [badgeOpen, setBadgeOpen] = useState(false);
  const [draft, setDraft] = useState(() => initial(user));

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['user', user.id] });
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const save = useMutation({
    mutationFn: () => {
      // Only what changed, blanks as null: clearing a field is a real edit.
      const before = initial(user);
      const changes: Record<string, string | null> = {};
      for (const { key } of FIELDS) {
        if (draft[key] !== before[key]) changes[key] = draft[key].trim() === '' ? null : draft[key].trim();
      }
      return api.updateUser(user.id, changes as PersonalDetails);
    },
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
  });

  const photo = useMutation({
    mutationFn: (file: File) => api.setUserPhoto(user.id, file),
    onSuccess: refresh,
  });
  const removePhoto = useMutation({ mutationFn: () => api.removeUserPhoto(user.id), onSuccess: refresh });

  // What the identification card needs; the photo can still be chosen in its dialog.
  const badgeMissing = [
    !user.birthPlace ? t('personal.birthPlace') : null,
    !user.dateOfBirth ? t('personal.dateOfBirth') : null,
  ].filter(Boolean);
  const shown = (key: FieldKey, type?: 'date') => {
    const value = user[key] as string | null | undefined;
    if (!value) return '—';
    return type === 'date' ? dateShort(value) : value;
  };

  return (
    <Card>
      <div className="flex items-center gap-4 pb-4 mb-4 border-b border-border">
        <Avatar name={fullName(user)} size={72} photoUrl={user.photoUrl} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{t('personal.photo')}</div>
          {canEdit ? (
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                className="btn-secondary !py-1.5 !px-3 text-sm"
                disabled={photo.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {photo.isPending ? '…' : t(user.photoUrl ? 'personal.changePhoto' : 'personal.uploadPhoto')}
              </button>
              {user.photoUrl ? (
                <button
                  type="button"
                  className="btn-secondary !py-1.5 !px-3 text-sm"
                  disabled={removePhoto.isPending}
                  onClick={() => removePhoto.mutate()}
                >
                  {t('personal.removePhoto')}
                </button>
              ) : null}
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) photo.mutate(file);
                }}
              />
            </div>
          ) : null}
          {photo.error ? <p className="text-xs text-danger mt-1">{t('personal.photoFailed')}</p> : null}
        </div>
      </div>

      {editing ? (
        <form
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {FIELDS.map(({ key, type, wide }) => (
            <label key={key} className={`block ${wide ? 'sm:col-span-2' : ''}`}>
              <span className="label-text block mb-1">{t(`personal.${key}` as TranslationKey)}</span>
              <input
                className="input"
                type={type ?? 'text'}
                value={draft[key]}
                required={key === 'firstName' || key === 'lastName'}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            </label>
          ))}
          {save.error ? (
            <div className="sm:col-span-2">
              <ErrorNotice error={save.error} />
            </div>
          ) : null}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setDraft(initial(user));
                setEditing(false);
              }}
            >
              {t('personal.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {t('personal.save')}
            </button>
          </div>
        </form>
      ) : (
        <>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {FIELDS.filter(({ key }) => key !== 'firstName' && key !== 'lastName').map(({ key, type }) => (
              <div key={key} className="contents">
                <dt className="text-muted">{t(`personal.${key}` as TranslationKey)}</dt>
                <dd className="text-end break-words min-w-0">{shown(key, type)}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 rounded border border-border bg-surface-alt px-3 py-2.5 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{t('badge.title')}</div>
              <div className={`text-xs ${badgeMissing.length > 0 ? 'text-warning' : 'text-muted'}`}>
                {badgeMissing.length > 0
                  ? t('badge.needs', { fields: badgeMissing.join(', ').toLowerCase() })
                  : user.photoUrl
                    ? t('badge.ready')
                    : t('badge.readyNoPhoto')}
              </div>
            </div>
            {canEdit ? (
              <button
                type="button"
                className={badgeMissing.length > 0 ? 'btn-secondary' : 'btn-primary'}
                disabled={badgeMissing.length > 0}
                onClick={() => setBadgeOpen(true)}
              >
                {t('badge.generate')}
              </button>
            ) : null}
          </div>
          <BadgeDialog user={user} open={badgeOpen} onClose={() => setBadgeOpen(false)} />
          {canEdit ? (
            <div className="flex justify-end mt-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setDraft(initial(user));
                  setEditing(true);
                }}
              >
                {t('personal.edit')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
